import { useEffect, useRef, useState } from 'react'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import { ACTIVE_POLL_MS, JobScreen, SYNC_TAGS_AFTER_JOB, SYNC_WINDOWS, shownError, syncApi, syncApplyRetry, syncJob, type ShownError } from '@krp/sync-ui'

import { useAppDispatch } from '../../app/hooks'
import { queryState, useOperationIds, useSyncSources } from './syncKit'

/**
 * Ход фоновой задачи применения. Пока задача ждёт очереди или идёт, её состояние перечитывается раз в 2 с; когда закончилась,
 * расхождения, счётчики и журнал перечитываются один раз — что применилось, уходит из списка само.
 */
export function SyncJobBinding() {
  const { params } = useWindow()
  const jobId = params.job ?? ''
  const dispatch = useAppDispatch()
  const { nameOf } = useSyncSources()
  const [pollMs, setPollMs] = useState(0)
  const job = syncJob.useQuery({ jobId }, { skip: jobId === '', pollingInterval: pollMs })
  const active = job.data === undefined || job.data.state === 'queued' || job.data.state === 'running'
  const wantedPoll = active && !job.isError ? ACTIVE_POLL_MS : 0
  if (wantedPoll !== pollMs) setPollMs(wantedPoll)

  // Задача могла закончиться раньше, чем окно впервые спросило о ней, поэтому перечитывание — на первый же итог, один раз на задачу.
  const refreshedFor = useRef<string | null>(null)
  const finished = job.data !== undefined && !active
  useEffect(() => {
    if (!finished || refreshedFor.current === jobId) return
    refreshedFor.current = jobId
    dispatch(syncApi.util.invalidateTags(SYNC_TAGS_AFTER_JOB))
  }, [finished, dispatch, jobId])

  // Повтор неудавшихся строк — новой задачей, её ход открывается в окне задачи.
  const openWindow = useOpenWindow()
  const { operationFor, settled } = useOperationIds()
  const [retry] = syncApplyRetry.useMutation()
  const [retryBusy, setRetryBusy] = useState(false)
  const [retryError, setRetryError] = useState<ShownError | null>(null)
  const runRetry = () => {
    const intent = `retry:${jobId}`
    setRetryBusy(true)
    setRetryError(null)
    void retry({ jobId, operationId: operationFor(intent) })
      .unwrap()
      .then((next) => {
        settled(intent)
        openWindow(SYNC_WINDOWS.job, { job: next.id })
      })
      .catch((error: unknown) => setRetryError(shownError('Повтор не отправлен', error)))
      .finally(() => setRetryBusy(false))
  }

  const state = queryState(job, 'Не удалось узнать, как идёт задача')
  return (
    <JobScreen
      vm={{ status: state.status, loadError: state.loadError, job: job.data ?? null, nameOf, retryBusy, retryError }}
      actions={{ reload: () => void job.refetch(), retry: runRetry }}
    />
  )
}
